import { useEffect, useRef, useState } from "react";

/**
 * CustomCursor — premium glassmorphism circle that follows the mouse.
 * Only enabled on devices with fine pointer (desktop). Mobile devices use OS cursor.
 * The default cursor is hidden via CSS.
 */
export const CustomCursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [variant, setVariant] = useState("default"); // 'default' | 'pointer'

  useEffect(() => {
    // Enable only on devices with fine pointer (no touch devices)
    const mq = window.matchMedia("(pointer: fine)");
    setEnabled(mq.matches);
    const onChange = (e) => setEnabled(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    document.documentElement.classList.add("nautiqo-custom-cursor");

    let mouseX = 0, mouseY = 0;
    let ringX = 0, ringY = 0;
    let raf = null;

    const onMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${mouseX - 4}px, ${mouseY - 4}px, 0)`;
      }
      // Detect interactive element under cursor
      const el = e.target.closest("a, button, input, [role='button'], [data-testid$='-btn']");
      setVariant(el ? "pointer" : "default");
    };

    const animate = () => {
      // Ring lerp follow (smoother)
      const ease = 0.18;
      ringX += (mouseX - ringX) * ease;
      ringY += (mouseY - ringY) * ease;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ringX - 16}px, ${ringY - 16}px, 0)`;
      }
      raf = requestAnimationFrame(animate);
    };

    window.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
      document.documentElement.classList.remove("nautiqo-custom-cursor");
    };
  }, [enabled]);

  if (!enabled) return null;

  const ringScale = variant === "pointer" ? 1.7 : 1;
  const dotScale = variant === "pointer" ? 0 : 1;

  return (
    <>
      {/* Outer ring (lerped, glassy) */}
      <div
        ref={ringRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0, left: 0,
          width: 32, height: 32,
          borderRadius: "9999px",
          border: "1.5px solid rgba(96, 165, 250, 0.6)",
          backgroundColor: "rgba(59, 130, 246, 0.08)",
          backdropFilter: "blur(2px)",
          pointerEvents: "none",
          zIndex: 9999,
          transition: "transform 0.18s ease-out, opacity 0.2s",
          willChange: "transform",
          transformOrigin: "center",
        }}
        className={variant === "pointer" ? "nautiqo-cursor-ring nautiqo-cursor-ring-pointer" : "nautiqo-cursor-ring"}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "9999px",
            transform: `scale(${ringScale})`,
            transition: "transform 0.18s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        />
      </div>
      {/* Inner dot (immediate follow) */}
      <div
        ref={dotRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: 0, left: 0,
          width: 8, height: 8,
          borderRadius: "9999px",
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          pointerEvents: "none",
          zIndex: 10000,
          transform: "translate3d(-100px, -100px, 0)",
          willChange: "transform",
          opacity: dotScale,
          transition: "opacity 0.18s, transform 0.05s linear",
        }}
      />
    </>
  );
};
